import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import {
  PROJECT_TYPE_OPTIONS,
  WELL_KNOWN_METADATA_KEYS,
  type ProjectTypeOption,
} from './PatternBuilder/helpers';
import { Touchable } from './touchable';

const CUSTOM_OTHER_SENTINEL = '__other__';

const IDENT_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

interface CustomFieldRow {
  uid: string;
  key: string;
  value: string;
}

interface MetadataEditorProps {
  metadata: Record<string, string> | undefined;
  onChange: (next: Record<string, string>) => void;
  /** Called whenever the editor's validity changes. Builders use it to disable Save. */
  onValidityChange?: (valid: boolean) => void;
}

function partition(metadata: Record<string, string> | undefined): {
  wellKnown: Record<string, string>;
  custom: CustomFieldRow[];
} {
  const wellKnown: Record<string, string> = {};
  const custom: CustomFieldRow[] = [];
  if (!metadata) return { wellKnown, custom };
  for (const [key, value] of Object.entries(metadata)) {
    if ((WELL_KNOWN_METADATA_KEYS as readonly string[]).includes(key)) {
      wellKnown[key] = value;
    } else {
      custom.push({ uid: crypto.randomUUID(), key, value });
    }
  }
  return { wellKnown, custom };
}

function combine(
  wellKnown: Record<string, string>,
  custom: CustomFieldRow[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of WELL_KNOWN_METADATA_KEYS) {
    const v = wellKnown[k];
    if (typeof v === 'string' && v.trim() !== '') out[k] = v;
  }
  for (const row of custom) {
    if (row.key && row.value.trim() !== '') {
      out[row.key] = row.value;
    }
  }
  return out;
}

export const MetadataEditor: React.FC<MetadataEditorProps> = ({
  metadata,
  onChange,
  onValidityChange,
}) => {
  const { t } = useTranslation('builder');
  // `metadata` is read once at mount; this editor is mounted per-pattern in
  // PatternBuilder, so prop swaps don't occur. If a future caller mounts it
  // across patterns, lift state up or add a metadata-ref reset effect.
  const initial = useMemo(() => partition(metadata), [metadata]);

  const [open, setOpen] = useState(true);
  const [wellKnown, setWellKnown] = useState<Record<string, string>>(initial.wellKnown);
  const [custom, setCustom] = useState<CustomFieldRow[]>(initial.custom);
  const [projectTypeIsOther, setProjectTypeIsOther] = useState(() => {
    const v = initial.wellKnown.project_type ?? '';
    return v !== '' && !(PROJECT_TYPE_OPTIONS as readonly string[]).includes(v);
  });

  // Compute and surface validity. Errors:
  //  - empty custom key
  //  - non-identifier custom key
  //  - custom key collides with another custom key or any well-known key
  const customErrors = useMemo(() => {
    const errs = new Map<string, string>();
    const seen = new Set<string>(WELL_KNOWN_METADATA_KEYS);
    for (const row of custom) {
      const k = row.key;
      if (k === '') {
        errs.set(row.uid, t('metadata.errors.keyEmpty', 'Key must not be empty.'));
        continue;
      }
      if (!IDENT_RE.test(k)) {
        errs.set(
          row.uid,
          t(
            'metadata.errors.keyInvalid',
            'Key must start with a letter or underscore and contain only letters, digits, and underscores.',
          ),
        );
        continue;
      }
      if (seen.has(k)) {
        errs.set(row.uid, t('metadata.errors.keyDuplicate', 'Key already in use.'));
        continue;
      }
      seen.add(k);
    }
    return errs;
  }, [custom, t]);

  const valid = customErrors.size === 0;
  useEffect(() => {
    onValidityChange?.(valid);
  }, [valid, onValidityChange]);

  // Whenever local state changes, emit the combined record upstream.
  useEffect(() => {
    onChange(combine(wellKnown, custom));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wellKnown, custom]);

  const setWellKnownField = (key: string, value: string) => {
    setWellKnown((prev) => ({ ...prev, [key]: value }));
  };

  const onProjectTypeSelect = (raw: string) => {
    if (raw === CUSTOM_OTHER_SENTINEL) {
      setProjectTypeIsOther(true);
      setWellKnownField('project_type', wellKnown.project_type ?? '');
      return;
    }
    setProjectTypeIsOther(false);
    setWellKnownField('project_type', raw);
  };

  const projectTypeSelectValue = projectTypeIsOther
    ? CUSTOM_OTHER_SENTINEL
    : ((wellKnown.project_type as ProjectTypeOption | '' | undefined) ?? '');

  return (
    <div className="bg-card rounded-2xl border border-line shadow-sm overflow-hidden">
      <div className="w-full flex items-center justify-between p-4 bg-primary-soft/60 border-b border-primary-soft">
        <Touchable
          haptic="softTap"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-primary-strong"
        >
          {t('metadata.title', 'Metadata')}
        </Touchable>
        <Touchable
          haptic="softTap"
          onClick={() => setOpen((v) => !v)}
          className="text-xs text-ink-faint"
        >
          {open ? t('common.hide', 'Hide') : t('common.show', 'Show')}
        </Touchable>
      </div>
      {open && (
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] items-center gap-3">
            <label className="text-xs font-bold text-ink-faint" htmlFor="meta-project-type">
              {t('metadata.projectType', 'Project type')}
            </label>
            <div className="flex flex-col gap-2">
              <select
                id="meta-project-type"
                value={projectTypeSelectValue}
                onChange={(e) => onProjectTypeSelect(e.target.value)}
                className="text-sm bg-card border border-line rounded px-2 py-1 text-ink"
              >
                <option value="">{t('metadata.noneOption', '(none)')}</option>
                {PROJECT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt[0].toUpperCase() + opt.slice(1)}
                  </option>
                ))}
                <option value={CUSTOM_OTHER_SENTINEL}>{t('metadata.otherOption', 'Other…')}</option>
              </select>
              {projectTypeIsOther && (
                <input
                  type="text"
                  aria-label={t('metadata.customProjectType', 'Custom project type')}
                  value={wellKnown.project_type ?? ''}
                  onChange={(e) => setWellKnownField('project_type', e.target.value)}
                  placeholder="custom type"
                  className="text-sm bg-card border border-line rounded px-2 py-1 text-ink"
                />
              )}
            </div>

            <label className="text-xs font-bold text-ink-faint" htmlFor="meta-yarn-weight">
              {t('metadata.yarnWeight', 'Yarn weight')}
            </label>
            <input
              id="meta-yarn-weight"
              type="text"
              value={wellKnown.yarn_weight ?? ''}
              onChange={(e) => setWellKnownField('yarn_weight', e.target.value)}
              placeholder="fingering, DK, worsted…"
              className="text-sm bg-card border border-line rounded px-2 py-1 text-ink"
            />

            <label className="text-xs font-bold text-ink-faint" htmlFor="meta-gauge">
              {t('metadata.gauge', 'Gauge')}
            </label>
            <input
              id="meta-gauge"
              type="text"
              value={wellKnown.gauge ?? ''}
              onChange={(e) => setWellKnownField('gauge', e.target.value)}
              placeholder="32 sts / 4 in"
              className="text-sm bg-card border border-line rounded px-2 py-1 text-ink"
            />

            <label className="text-xs font-bold text-ink-faint" htmlFor="meta-needle-size">
              {t('metadata.needleSize', 'Needle size')}
            </label>
            <input
              id="meta-needle-size"
              type="text"
              value={wellKnown.needle_size ?? ''}
              onChange={(e) => setWellKnownField('needle_size', e.target.value)}
              placeholder="US 1.5 / 2.5 mm"
              className="text-sm bg-card border border-line rounded px-2 py-1 text-ink"
            />
          </div>

          {custom.length > 0 && (
            <div className="pt-2 border-t border-line">
              <p className="text-[10px] font-bold uppercase tracking-widest text-ink-faint mb-2">
                {t('metadata.customFields', 'Custom fields')}
              </p>
              <div className="space-y-2">
                {custom.map((row, idx) => {
                  const err = customErrors.get(row.uid);
                  return (
                    <div key={row.uid} className="flex flex-wrap items-start gap-2">
                      <input
                        type="text"
                        aria-label={t('metadata.customFieldKey', 'Custom field {{n}} key', {
                          n: idx + 1,
                        })}
                        value={row.key}
                        onChange={(e) => {
                          const k = e.target.value;
                          setCustom((prev) =>
                            prev.map((r) => (r.uid === row.uid ? { ...r, key: k } : r)),
                          );
                        }}
                        placeholder="key"
                        className="font-mono text-sm bg-card border border-line rounded px-2 py-1 text-ink w-44"
                      />
                      <span className="self-center text-ink-faint">=</span>
                      <input
                        type="text"
                        aria-label={t('metadata.customFieldValue', 'Custom field {{n}} value', {
                          n: idx + 1,
                        })}
                        value={row.value}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCustom((prev) =>
                            prev.map((r) => (r.uid === row.uid ? { ...r, value: v } : r)),
                          );
                        }}
                        placeholder="value"
                        className="text-sm bg-card border border-line rounded px-2 py-1 text-ink flex-grow"
                      />
                      <Touchable
                        haptic="softTap"
                        onClick={() => setCustom((prev) => prev.filter((r) => r.uid !== row.uid))}
                        className="p-1 text-danger hover:bg-danger-soft rounded"
                        aria-label={t('metadata.deleteCustomField', 'Delete custom field {{key}}', {
                          key: row.key || idx + 1,
                        })}
                      >
                        <X className="w-4 h-4" />
                      </Touchable>
                      {err && <p className="basis-full text-xs text-danger">{err}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="pt-2">
            <Touchable
              haptic="tap"
              onClick={() =>
                setCustom((prev) => [...prev, { uid: crypto.randomUUID(), key: '', value: '' }])
              }
              className="px-3 py-1.5 bg-primary text-white rounded-xl text-xs font-bold hover:scale-105 transition-transform shadow-sm"
            >
              {t('metadata.addCustomField', '+ Add custom field')}
            </Touchable>
          </div>
        </div>
      )}
    </div>
  );
};
